import { CheckOutlined } from '@ant-design/icons';
import {
  ProductBatchUpdateHistoryDoc,
  ProductChangeItem,
  ProductDoc,
  ProductStateCode,
  productStates,
} from '@gooduncles/gu-app-schema';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button, Input, Modal, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import { FC, useCallback, useMemo, useState } from 'react';
import { productTableTooltipMap } from 'src/constants';
import { useAuth } from 'src/stores/auth-context';

import { productStateFormatter } from 'src/utils/ag-grid-util';
import { AuditLogger } from 'src/utils/audit-logger';
import { formatDate } from 'src/utils/date-util';
import {
  batchEnd,
  batchStart,
  createProductBatchUpdateHistory,
  updateProduct,
  updateProductBatchUpdateHistory,
} from 'src/utils/firebase/firebase-short-cut';

const auditLogger = AuditLogger.getInstance();

import useProductBatchUpdateHistories from 'src/hooks/subscription/useProductBatchUpdateHistories';
import useProductsWithOptions from 'src/hooks/subscription/useProductsWithOptions';

// 상품 그리드 기본 컬럼 설정
const defaultColDef: ColDef<ProductDoc> = {
  sortable: true,
  resizable: true,
  filter: true,
  floatingFilter: true,
};

// 상품 그리드 컴포넌트
interface ProductGridProps {
  rowData: ProductDoc[];
  onSelectionChanged: (selectedRows: ProductDoc[]) => void;
  gridRef: (grid: AgGridReact<ProductDoc> | null) => void;
}

const ProductGrid: FC<ProductGridProps> = ({ rowData, onSelectionChanged, gridRef }) => {
  const columnDefs: (ColDef<ProductDoc> | ColGroupDef<ProductDoc>)[] = useMemo(
    () => [
      {
        width: 50,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        filter: false,
      },
      {
        headerName: 'productId',
        field: '_id',
        width: 200,
        hide: true,
      },
      { headerName: '코드', field: 'productId', width: 98, filter: 'agTextColumnFilter' },
      {
        headerName: '품명',
        field: 'fullName',
        filter: 'agTextColumnFilter',
        width: 240,
      },
      {
        headerName: '판매상태',
        field: 'state',
        tooltipField: 'state',
        headerTooltip: productTableTooltipMap.state,
        valueFormatter: productStateFormatter,
        filterParams: {
          valueFormatter: productStateFormatter,
        },
        width: 120,
      },
      {
        headerName: '거래처',
        field: 'supplier.name',
        width: 120,
      },
    ],
    [],
  );

  return (
    <AgGridReact
      ref={gridRef}
      rowData={rowData}
      // rowId를 지정해주지 않으면 데이터 변경시 refresh가 발생한다.
      getRowId={(params) => params.data._id}
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      tooltipShowDelay={500}
      // 편집 완료후 스크롤 이동을 막는다.
      suppressScrollOnNewData={true}
      alwaysShowHorizontalScroll={true}
      // 행 선택을 허용한다.
      rowSelection='multiple'
      onSelectionChanged={(e) => {
        onSelectionChanged(e.api.getSelectedRows());
      }}
      // 행 선택을 켜놓더라도 개별 셀의 내용을 복사할 수 있도록한다.
      enableCellTextSelection={true}
    />
  );
};

// 상태 변경 사유 모달 컴포넌트
interface ProductStateChangeModalProps {
  visible: boolean;
  selectedRows: ProductDoc[];
  changeReason: string;
  onChangeReason: (reason: string) => void;
  onOk: () => void;
  onCancel: () => void;
}

const ProductStateChangeModal: FC<ProductStateChangeModalProps> = ({
  visible,
  selectedRows,
  changeReason,
  onChangeReason,
  onOk,
  onCancel,
}) => {
  return (
    <Modal title='상태 변경 사유' open={visible} onOk={onOk} onCancel={onCancel} okText='변경' cancelText='취소'>
      <p>선택한 {selectedRows.length}개 상품의 상태를 변경하는 사유를 입력해주세요.</p>
      <Input.TextArea
        rows={4}
        value={changeReason}
        onChange={(e) => onChangeReason(e.target.value)}
        placeholder='변경 사유를 입력해주세요'
      />
    </Modal>
  );
};

// 변경 이력 모달 컴포넌트
interface ProductHistoryModalProps {
  visible: boolean;
  histories: ProductBatchUpdateHistoryDoc[];
  onCancel: () => void;
  onRollback: (history: ProductBatchUpdateHistoryDoc) => void;
  rollbackLoading: string | null;
}

const ProductHistoryModal: FC<ProductHistoryModalProps> = ({
  visible,
  histories,
  onCancel,
  onRollback,
  rollbackLoading,
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  // 모달이 닫힐 때 확장된 행 상태 초기화
  const handleCancel = () => {
    setExpandedRowKeys([]);
    onCancel();
  };

  const historyColumns = useMemo(
    () => [
      {
        title: '변경 일시',
        dataIndex: 'timestamp',
        key: 'timestamp',
        render: (text: string) => {
          const date = new Date(text);
          return formatDate(date, 'yyyy-MM-dd HH:mm:ss');
        },
        sorter: (a: ProductBatchUpdateHistoryDoc, b: ProductBatchUpdateHistoryDoc) => {
          return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        },
        defaultSortOrder: 'descend' as SortOrder,
      },
      {
        title: '변경 사유',
        dataIndex: 'reason',
        key: 'reason',
      },
      {
        title: '변경 상품 수',
        dataIndex: 'products',
        key: 'products',
        render: (products: ProductChangeItem[]) => products.length,
      },
      {
        title: '변경자',
        dataIndex: 'userId',
        key: 'userId',
      },
      {
        title: '롤백',
        dataIndex: 'isRolledBack',
        key: 'isRolledBack',
        render: (isRolledBack: boolean) =>
          isRolledBack ? <Tag color='green' icon={<CheckOutlined />} /> : <Tag color='default' />,
      },
      {
        title: '액션',
        key: 'action',
        render: (_: any, record: ProductBatchUpdateHistoryDoc) => (
          <Space size='middle'>
            {!record.isRolledBack && !record.isCreatedByRollback && (
              <Popconfirm
                title='롤백 확인'
                description='정말 이 변경을 롤백하시겠습니까? 변경 내용이 이전 상태로 되돌아갑니다.'
                onConfirm={() => onRollback(record)}
                okText='예'
                cancelText='아니오'
              >
                <Button type='link' loading={rollbackLoading === record._id} disabled={record.isRolledBack}>
                  롤백
                </Button>
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    [rollbackLoading, onRollback],
  );

  return (
    <Modal
      title='상품 상태 일괄 변경 이력'
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={1000}
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
        },
      }}
    >
      <Table
        dataSource={histories.map((history) => ({ ...history, key: history._id }))}
        columns={historyColumns}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (expandedRows) => {
            setExpandedRowKeys(expandedRows as string[]);
          },
          expandedRowRender: (record: ProductBatchUpdateHistoryDoc) => (
            <div style={{ margin: 0 }}>
              <Typography.Title level={5}>변경된 상품 목록</Typography.Title>
              <Table
                size='small'
                dataSource={record.products.map((product, index) => ({ ...product, key: index }))}
                columns={[
                  {
                    title: '상품 ID',
                    dataIndex: 'productId',
                    key: 'productId',
                  },
                  {
                    title: '이전 상태',
                    dataIndex: 'previousValue',
                    key: 'previousValue',
                    render: (state: ProductStateCode) => productStates[state] || state,
                  },
                  {
                    title: '변경 상태',
                    dataIndex: 'newValue',
                    key: 'newValue',
                    render: (state: ProductStateCode) => productStates[state] || state,
                  },
                ]}
                pagination={false}
              />
            </div>
          ),
        }}
      />
    </Modal>
  );
};

// 메인 컴포넌트
const ProductTableSelector: FC = () => {
  const { user } = useAuth();
  const rowData = useProductsWithOptions({ mergeSupplier: true });
  const productBatchUpdateHistories = useProductBatchUpdateHistories();
  const [loading, setLoading] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<ProductDoc[]>([]);
  const [selectedState, setSelectedState] = useState<ProductStateCode>();
  const [gridRef, setGridRef] = useState<AgGridReact<ProductDoc> | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [changeReason, setChangeReason] = useState('');
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);

  // 상태 변경 사유 모달 표시
  const showReasonModal = () => {
    if (!selectedRows.length || !selectedState) {
      message.warning('상품과 변경할 상태를 선택해주세요.');
      return;
    }
    setIsModalVisible(true);
  };

  // 상태 변경 사유 모달 취소
  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  // 상태 변경 사유 모달 확인
  const handleModalOk = async () => {
    if (!changeReason.trim()) {
      message.warning('변경 사유를 입력해주세요.');
      return;
    }
    await updateProductsStatus();
    setIsModalVisible(false);
  };

  // 상품 상태 일괄 변경
  const updateProductsStatus = useCallback(async () => {
    if (!user) {
      message.error('로그인 정보가 없습니다.');
      return;
    }
    if (!selectedRows.length || !selectedState) {
      message.warning('상품과 변경할 상태를 선택해주세요.');
      return;
    }

    const products: ProductChangeItem[] = selectedRows.map((product) => ({
      productId: product._id,
      previousValue: product.state,
      newValue: selectedState,
      isRolledBack: false,
    }));
    setLoading(true);
    try {
      batchStart();
      for (const product of selectedRows) {
        await updateProduct(product._id, { state: selectedState }, { bBatch: true, audit: { suppress: true } });
      }
      await batchEnd();
      await createProductBatchUpdateHistory(
        {
          timestamp: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
          products,
          field: 'state',
          reason: changeReason || '상태 변경',
          userId: user._id,
          isRolledBack: false,
          rollbackTimestamp: null,
          rollbackUserId: null,
          isCreatedByRollback: false,
        },
        { audit: { suppress: true } },
      );
      auditLogger.log({
        action: 'BULK_UPDATE',
        entity: 'product',
        entityId: `batch-${products.length}`,
        summary: `상품 상태 일괄 변경 (${selectedRows.length}개) → ${selectedState}`,
        changes: products.map((p) => ({ field: 'state', before: p.previousValue, after: p.newValue })),
        metadata: { reason: changeReason || '상태 변경', productIds: products.map((p) => p.productId) },
      });
      message.success(`${selectedRows.length}개 상품의 상태가 변경되었습니다.`);
      // 선택 초기화
      setSelectedRows([]);
      setSelectedState(undefined);
      setChangeReason('');
      // 그리드 새로고침
      if (gridRef?.api) {
        gridRef.api.deselectAll();
      }
    } catch (error) {
      message.error('상태 변경 중 오류가 발생했습니다.');
      console.error('상태 변경 오류:', error);
    } finally {
      setLoading(false);
    }
  }, [user, selectedRows, selectedState, gridRef, changeReason]);

  // 변경 이력 모달 표시
  const showHistoryModal = () => {
    setIsHistoryModalVisible(true);
  };

  // 변경 이력 모달 취소
  const handleHistoryModalCancel = () => {
    setIsHistoryModalVisible(false);
  };

  // 롤백 처리
  const handleRollback = useCallback(
    async (history: ProductBatchUpdateHistoryDoc) => {
      if (!user) {
        message.error('로그인 정보가 없습니다.');
        return;
      }

      if (history.isRolledBack) {
        message.warning('이미 롤백된 변경 내역입니다.');
        return;
      }

      setRollbackLoading(history._id);
      try {
        batchStart();
        // 각 상품의 상태를 이전 상태로 되돌림
        for (const product of history.products) {
          if (product.previousValue) {
            await updateProduct(
              product.productId,
              { state: product.previousValue as ProductStateCode },
              { bBatch: true, audit: { suppress: true } },
            );
          }
        }
        await batchEnd();

        const timestamp = formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900");
        // 기존 변경 이력에 롤백 정보 업데이트
        await updateProductBatchUpdateHistory(
          history._id,
          {
            isRolledBack: true,
            rollbackTimestamp: timestamp,
            rollbackUserId: user._id,
          },
          { audit: { suppress: true } },
        );

        // 롤백 정보 업데이트
        await createProductBatchUpdateHistory(
          {
            timestamp,
            products: history.products.map((product) => ({
              ...product,
              previousValue: product.newValue,
              newValue: product.previousValue,
              isRolledBack: true,
            })),
            field: 'state',
            reason: `롤백: ${history.reason}`,
            userId: user._id,
            isRolledBack: false,
            rollbackTimestamp: null,
            rollbackUserId: null,
            isCreatedByRollback: true,
          },
          { audit: { suppress: true } },
        );

        auditLogger.log({
          action: 'BULK_UPDATE',
          entity: 'product',
          entityId: `rollback-${history._id}`,
          summary: `상품 상태 롤백 (${history.products.length}개) - 원인: ${history.reason}`,
          changes: history.products.map((p) => ({ field: 'state', before: p.newValue, after: p.previousValue })),
          metadata: { originalHistoryId: history._id, productIds: history.products.map((p) => p.productId) },
        });
        message.success('성공적으로 롤백되었습니다.');

        // 그리드 새로고침
        if (gridRef?.api) {
          gridRef.api.refreshCells();
        }
      } catch (error) {
        message.error('롤백 중 오류가 발생했습니다.');
        console.error('롤백 오류:', error);
      } finally {
        setRollbackLoading(null);
      }
    },
    [user, gridRef],
  );

  // 그리드 선택 변경 처리
  const handleSelectionChanged = useCallback((rows: ProductDoc[]) => {
    setSelectedRows(rows);
    setSelectedState(undefined);
  }, []);

  return (
    <div className='ag-theme-alpine height100 flexColumn'>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select
            style={{ width: 200 }}
            placeholder='변경할 상태 선택'
            value={selectedState}
            onChange={(value) => setSelectedState(value as ProductStateCode)}
            options={Object.entries(productStates).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <Button
            type='primary'
            onClick={showReasonModal}
            loading={loading}
            disabled={!selectedRows.length || !selectedState}
          >
            선택된 상품: {selectedRows.length}개 상태 일괄 변경
          </Button>
        </div>
        <Button onClick={showHistoryModal}>변경 이력 보기</Button>
      </div>

      <ProductGrid rowData={rowData} onSelectionChanged={handleSelectionChanged} gridRef={setGridRef} />

      <ProductStateChangeModal
        visible={isModalVisible}
        selectedRows={selectedRows}
        changeReason={changeReason}
        onChangeReason={setChangeReason}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
      />

      <ProductHistoryModal
        visible={isHistoryModalVisible}
        histories={productBatchUpdateHistories}
        onCancel={handleHistoryModalCancel}
        onRollback={handleRollback}
        rollbackLoading={rollbackLoading}
      />
    </div>
  );
};

export default ProductTableSelector;
